import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const ALL_VALUE = "$__all";

const sameValue = (a, b) => String(a) === String(b);

const asArray = (value) =>
  Array.isArray(value)
    ? value
    : value === null || value === undefined
      ? []
      : [value];

export const VariableRenderer = ({
  definition,
  value,
  options = [],
  loading = false,
  onChange,
}) => {
  const items = useMemo(() => {
    if (!definition.incluye_todos || !definition.multi) return options;

    return [
      { __value: ALL_VALUE, __text: "Todos" },
      ...options.filter((option) => !sameValue(option.__value, ALL_VALUE)),
    ];
  }, [definition.incluye_todos, options]);

  const selected = definition.multi
    ? asArray(value)
        .map((current) =>
          items.find((option) => sameValue(option.__value, current))
        )
        .filter(Boolean)
    : items.find((option) => sameValue(option.__value, value)) || null;

  if (definition.tipo === "constant" || definition.oculto) {
    return null;
  }

  const label = definition.descripcion ? (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      <span>{definition.etiqueta || definition.nombre}</span>
      <Tooltip title={definition.descripcion} arrow placement="top">
        <InfoOutlinedIcon
          fontSize="inherit"
          sx={{ cursor: "help", fontSize: 15 }}
        />
      </Tooltip>
    </Box>
  ) : (
    definition.etiqueta || definition.nombre
  );

  if (definition.tipo === "text") {
    return (
      <TextField
        size="small"
        label={label}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        sx={{ minWidth: 210, flex: "1 1 210px" }}
      />
    );
  }

  return (
    <Autocomplete
      multiple={definition.multi}
      disableCloseOnSelect={definition.multi}
      options={items}
      value={selected}
      loading={loading}
      getOptionLabel={(option) => option?.__text || ""}
      isOptionEqualToValue={(option, selectedOption) =>
        sameValue(option.__value, selectedOption.__value)
      }
      onChange={(event, nextSelected) => {
        if (!definition.multi) {
          onChange(nextSelected?.__value ?? null);
          return;
        }

        const selectedItems = asArray(nextSelected);
        const previousValues = asArray(value);
        const hadAll = previousValues.some((item) => sameValue(item, ALL_VALUE));
        const hasAll = selectedItems.some((item) => sameValue(item.__value, ALL_VALUE));

        if (hasAll && !hadAll) {
          onChange([ALL_VALUE]);
          return;
        }

        const values = selectedItems
          .map((item) => item.__value)
          .filter((item) => !sameValue(item, ALL_VALUE));

        if (!values.length && definition.incluye_todos) {
          onChange([ALL_VALUE]);
          return;
        }

        onChange(values);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ minWidth: 210, flex: "1 1 210px" }}
    />
  );
};
